/**
 * space_fire_groups.ts — TWO FIRE GROUPS over the fitted mounts (space campaign I, 2026-09-30; report §18.2 7th ¶).
 *
 * Two TRIGGERS, the ones the sector always had: PRIMARY (`fire` — Space / LMB / the pad) and SECONDARY
 * (`spaceMissile` — Z). A GROUP says what each trigger fires: the primary ∈ {twin bolts, homing missile}, the
 * secondary ∈ {homing missile, sensor jammer, nothing}; a mount sits on at most one trigger of a group. The cycle
 * action (`spaceFireGroup`) swaps group 1 ↔ 2. The defaults are today's sector exactly (group 1: bolts / missile) plus
 * the EW suite on group 2's secondary.
 *
 * NO SIMULTANEOUS VOLLEYS: a trigger fires ONE mount, through the mount's own function (`fire()` / `fireMissile()` /
 * the jammer request) — the shared cooldowns, the capacitor per volley and the missile cadence are those functions',
 * unchanged, so where the server judges a shot (slice D's validated war fire, by the projectile's mount) a group changes
 * nothing about damage or cadence. Bolts are automatic while the primary is held; a missile or the jammer fires once
 * per PRESS (a held trigger never streams the rack).
 *
 * THE FIT REVISION: a group is saved per hull with an FNV-1a over that hull's `ship_component` rows (slot, compId,
 * quality). A CUSTOM grouping is re-validated when the revision changes — a mount the new fit lacks drops to bolts
 * (primary) or nothing (secondary) and the pilot is told which; the untouched DEFAULTS keep today's behaviour (Z with
 * no rack says "No missile rack fitted", as it always did). Pure rules + the Loadout renderer; storage is per browser
 * (the server judges every shot by its mount and every jam by the fit — a stored grouping would change no outcome).
 */
import { keyHint } from './keybinds.js';

export type Mount = 'bolts' | 'missile' | 'jammer';
export type Slot = Mount | 'none';
export interface FireGroup { primary: Mount; secondary: Slot }
export interface FireGroupsState { v: 1; fitRev: string; active: 0 | 1; custom: boolean; groups: [FireGroup, FireGroup] }
export interface FitFacts { missiles: number; counters: number }

export const PRIMARY_MOUNTS: readonly Mount[] = ['bolts', 'missile'];
export const SECONDARY_SLOTS: readonly Slot[] = ['missile', 'jammer', 'none'];
export const MOUNT_LABEL: Record<Slot, string> = { bolts: 'twin bolts', missile: 'homing missile', jammer: 'sensor jammer', none: 'nothing' };
export const MOUNT_GLYPH: Record<Slot, string> = { bolts: '◉', missile: '◈', jammer: '≋', none: '·' };
const NEEDS: Record<Slot, string> = { bolts: '', missile: 'a Missile Rack', jammer: 'a Countermeasures pod (the EW suite)', none: '' };

export function defaultGroups(): [FireGroup, FireGroup] {
  return [{ primary: 'bolts', secondary: 'missile' }, { primary: 'bolts', secondary: 'jammer' }];
}
export function defaultState(fitRev = ''): FireGroupsState {
  return { v: 1, fitRev, active: 0, custom: false, groups: defaultGroups() };
}

/** Is the mount on this fit? Bolts need no slot (the Cannons component only multiplies them). */
export function mountFitted(m: Slot, fit: FitFacts): boolean {
  if (m === 'missile') return fit.missiles > 0;
  if (m === 'jammer') return fit.counters > 0;
  return true;
}
/** Why a mount cannot fire on this fit ('' when it can). */
export function mountProblem(m: Slot, fit: FitFacts): string {
  return mountFitted(m, fit) ? '' : `needs ${NEEDS[m]}`;
}

/** FNV-1a over the hull's fitted components — order-free (sorted), so the same fit always reads the same revision. */
export function fitRevision(comps: ReadonlyArray<{ compId: bigint | number | string; slot: string; quality: number }>): string {
  const parts = comps.map((c) => `${c.slot}:${String(c.compId)}:${c.quality}`).sort();
  let h = 0x811c9dc5;
  for (const ch of parts.join('|')) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

/** A grouping repaired to the rules: legal mounts on each trigger, never the same mount twice in a group. */
function legal(g: FireGroup): { g: FireGroup; changed: boolean } {
  const primary: Mount = PRIMARY_MOUNTS.includes(g.primary) ? g.primary : 'bolts';
  let secondary: Slot = SECONDARY_SLOTS.includes(g.secondary) ? g.secondary : 'none';
  if (secondary === primary) secondary = 'none';
  return { g: { primary, secondary }, changed: primary !== g.primary || secondary !== g.secondary };
}

/**
 * Re-validate a saved state against the hull's CURRENT fit. A custom grouping whose revision differs loses the mounts
 * the fit lacks (the notes say which); the defaults are kept as they are (today's behaviour); the revision is recorded.
 */
export function validateGroups(state: FireGroupsState, fit: FitFacts, fitRev: string): { state: FireGroupsState; notes: string[] } {
  const notes: string[] = [];
  const groups = state.groups.map((g) => legal(g).g) as [FireGroup, FireGroup];
  if (state.custom && state.fitRev !== fitRev) {
    groups.forEach((g, i) => {
      if (!mountFitted(g.primary, fit)) { notes.push(`Fire group ${i + 1}: the ${MOUNT_LABEL[g.primary]} left with the fit (${mountProblem(g.primary, fit)}) — its primary is twin bolts now`); g.primary = 'bolts'; }
      if (!mountFitted(g.secondary, fit)) { notes.push(`Fire group ${i + 1}: the ${MOUNT_LABEL[g.secondary]} left with the fit (${mountProblem(g.secondary, fit)}) — its secondary is empty now`); g.secondary = 'none'; }
      if (g.secondary === g.primary) g.secondary = 'none';
    });
  }
  const active: 0 | 1 = state.active === 1 ? 1 : 0;
  return { state: { v: 1, fitRev, active, custom: state.custom, groups }, notes };
}

/** Assign one trigger of one group (the Loadout's choice). Refused: a mount the fit lacks, an illegal trigger slot. */
export function assign(state: FireGroupsState, group: 0 | 1, trigger: 'primary' | 'secondary', m: Slot, fit: FitFacts): { state: FireGroupsState; error: string } {
  if (trigger === 'primary' && !PRIMARY_MOUNTS.includes(m as Mount)) return { state, error: `The primary trigger fires twin bolts or the homing missile, not the ${MOUNT_LABEL[m]}` };
  if (trigger === 'secondary' && !SECONDARY_SLOTS.includes(m)) return { state, error: `The secondary trigger cannot hold the ${MOUNT_LABEL[m]}` };
  if (m !== 'none' && !mountFitted(m, fit)) return { state, error: `The ${MOUNT_LABEL[m]} ${mountProblem(m, fit)}` };
  const groups = state.groups.map((g) => ({ ...g })) as [FireGroup, FireGroup];
  const g = groups[group];
  if (trigger === 'primary') { g.primary = m as Mount; if (g.secondary === g.primary) g.secondary = 'none'; }
  else { g.secondary = m; if (g.secondary === g.primary) g.primary = 'bolts'; if (g.secondary === g.primary) g.secondary = 'none'; }
  return { state: { ...state, custom: true, groups }, error: '' };
}

export function cycleGroup(state: FireGroupsState): FireGroupsState { return { ...state, active: state.active === 0 ? 1 : 0 }; }
/** The primary trigger's tick: bolts fire every tick it is held (`fire()`'s own cooldown paces them); a missile or the
 *  jammer fires on the PRESS edge only. Returns the mount to fire this tick, or null. */
export function primaryAction(state: FireGroupsState, held: boolean, wasHeld: boolean): Slot | null {
  const m = state.groups[state.active].primary;
  if (m === 'bolts') return held ? 'bolts' : null;
  return held && !wasHeld ? m : null;
}
export function triggerMount(state: FireGroupsState, trigger: 'primary' | 'secondary'): Slot {
  const g = state.groups[state.active];
  return trigger === 'primary' ? g.primary : g.secondary;
}

/** The HUD chip: the active group and what each trigger fires, with the CURRENT keys (THE ONE-KEY LAW). */
export function groupChip(state: FireGroupsState, fit: FitFacts): string {
  const g = state.groups[state.active];
  const part = (k: string, m: Slot): string => `${k} ${MOUNT_GLYPH[m]} ${MOUNT_LABEL[m]}${m !== 'none' && !mountFitted(m, fit) ? ' (not fitted)' : ''}`;
  return `GRP ${state.active + 1} · ${part(keyHint('fire'), g.primary)} · ${part(keyHint('spaceMissile'), g.secondary)} · ${keyHint('spaceFireGroup')} swaps`;
}
/** The cycle's toast. */
export function cycleToast(state: FireGroupsState, fit: FitFacts): string {
  return `Fire group ${state.active + 1} — ${groupChip(state, fit).replace(/^GRP \d · /, '')}`;
}

// ── Storage (per hull, per browser) ────────────────────────────────────────────────────────────────────────────────
const LS = (shipId: string): string => `atlas:fire-groups:${shipId}`;
export function loadGroups(shipId: string): FireGroupsState {
  try {
    const raw = localStorage.getItem(LS(shipId));
    if (raw) {
      const s = JSON.parse(raw) as Partial<FireGroupsState>;
      if (s && s.v === 1 && Array.isArray(s.groups) && s.groups.length === 2) {
        return {
          v: 1, fitRev: typeof s.fitRev === 'string' ? s.fitRev : '', active: s.active === 1 ? 1 : 0, custom: s.custom === true,
          groups: s.groups.map((g) => legal({ primary: (g?.primary ?? 'bolts') as Mount, secondary: (g?.secondary ?? 'none') as Slot }).g) as [FireGroup, FireGroup],
        };
      }
    }
  } catch { /* private mode / a corrupt save — the defaults stand */ }
  return defaultState();
}
/** Has this hull a stored grouping (vs the defaults)? */
export function hasSavedGroups(shipId: string): boolean {
  try { return localStorage.getItem(LS(shipId)) !== null; } catch { return false; }
}
let saveSeq = 0;
/** Bumps on every save in this page — a reader re-reads storage only when someone else (the dock's Loadout) saved. */
export function groupsSaveSeq(): number { return saveSeq; }
export function saveGroups(shipId: string, state: FireGroupsState): void {
  saveSeq++;
  try { localStorage.setItem(LS(shipId), JSON.stringify(state)); } catch { /* private mode */ }
}

// ── The Loadout's section (fleet_dock.ts) ──────────────────────────────────────────────────────────────────────────
export interface LoadoutCtx { shipId: string; fit: FitFacts; fitRev: string; onNote?: (text: string) => void }
/** Render the FIRE GROUPS section into the Loadout body: both groups, both triggers as selects (a mount the fit lacks
 *  is listed but disabled, with what it needs), the active group marked with text, the fit revision. */
export function renderFireGroups(body: HTMLElement, ctx: LoadoutCtx): void {
  const v = validateGroups(loadGroups(ctx.shipId), ctx.fit, ctx.fitRev);
  saveGroups(ctx.shipId, v.state);
  for (const n of v.notes) ctx.onNote?.(n);
  const st = v.state;
  const title = document.createElement('div');
  title.textContent = 'FIRE GROUPS';
  title.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.2px;margin-top:4px';
  title.dataset.fireGroups = '1';
  body.appendChild(title);
  const lead = document.createElement('div');
  lead.style.cssText = 'font:10px var(--ax-mono);color:var(--ax-soft)';
  lead.textContent = `Two triggers: primary ${keyHint('fire')} / left mouse, secondary ${keyHint('spaceMissile')}. ${keyHint('spaceFireGroup')} swaps the group in flight. One mount per trigger — the cooldowns and the capacitor per volley are the mount's own.`;
  body.appendChild(lead);
  for (const gi of [0, 1] as const) {
    const g = st.groups[gi];
    const r = document.createElement('div');
    r.dataset.fireGroup = String(gi + 1);
    r.style.cssText = 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:var(--ax-bg2);border:1px solid ' +
      (st.active === gi ? 'var(--ax-accent)' : 'var(--ax-line)') + ';border-radius:var(--ax-radius);padding:7px 10px';
    const name = document.createElement('b');
    name.textContent = `Group ${gi + 1}${st.active === gi ? ' ▸ active' : ''}`;
    name.style.cssText = 'min-width:110px;font:600 11px var(--ax-mono);color:var(--ax-text)';
    r.appendChild(name);
    for (const trig of ['primary', 'secondary'] as const) {
      const lab = document.createElement('label');
      lab.style.cssText = 'display:flex;align-items:center;gap:6px;font:11px var(--ax-mono);color:var(--ax-soft)';
      lab.textContent = trig === 'primary' ? `Primary (${keyHint('fire')})` : `Secondary (${keyHint('spaceMissile')})`;
      const sel = document.createElement('select');
      sel.dataset.trigger = `${gi + 1}:${trig}`;
      sel.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);font:11px var(--ax-mono);padding:3px 6px;min-height:26px';
      const opts: readonly Slot[] = trig === 'primary' ? PRIMARY_MOUNTS : SECONDARY_SLOTS;
      for (const m of opts) {
        const o = document.createElement('option');
        o.value = m;
        const why = m === 'none' ? '' : mountProblem(m, ctx.fit);
        o.textContent = `${MOUNT_GLYPH[m]} ${MOUNT_LABEL[m]}${why ? ` — ${why}` : ''}`;
        o.disabled = !!why;
        o.selected = (trig === 'primary' ? g.primary : g.secondary) === m;
        sel.appendChild(o);
      }
      sel.onchange = () => {
        const res = assign(loadGroups(ctx.shipId), gi, trig, sel.value as Slot, ctx.fit);
        if (res.error) { ctx.onNote?.(res.error); return; }
        saveGroups(ctx.shipId, { ...res.state, fitRev: ctx.fitRev });
        ctx.onNote?.(`Fire group ${gi + 1}: ${trig} → ${MOUNT_LABEL[sel.value as Slot]}`);
        sel.blur(); // the dock's 1 s re-render waits while a select has focus — hand it back
      };
      lab.appendChild(sel);
      r.appendChild(lab);
    }
    body.appendChild(r);
  }
  const foot = document.createElement('div');
  foot.style.cssText = 'font:10px var(--ax-mono);color:var(--ax-muted)';
  foot.textContent = `${st.custom ? 'Your grouping' : 'The default grouping'} · set for fit revision ${ctx.fitRev}${st.custom ? ' — a refit re-checks it and says what moved' : ''}`;
  body.appendChild(foot);
}
